const crud = (controller: string) => ({
  list: () => `/${controller}/list`,
  dictionary: () => `/${controller}/dictionary`,
  details: (id: string) => `/${controller}/${id}`,
  update: (id: string) => `/${controller}/${id}`,
  delete: (id: string) => `/${controller}/${id}`,
  create: () => `/${controller}`,
})

export const apiUrls = {
  dashboard: {
    orders: {
      money: () => "/dashboard/orders",
      status: () => "/dashboard/orders/status",
      details: (period: string) => `/dashboard/orders/details?period=${encodeURIComponent(period)}`,
    },
    accounts: (id = "") => `/dashboard/accounts/${id}`,
    refresh: () => `/dashboard/refresh`,
    orderAgents: () => `/dashboard/order-agents`,
    employee: () => "/dashboard/employee",
    salary: () => "/dashboard/salary",
    companyAge: () => "/dashboard/company-age",
    income: () => "/dashboard/income",
    engineers: () => "/dashboard/engineers",
    dividends: () => "/dashboard/dividends",
    production: () => "/dashboard/production",
    profitability: () => "/dashboard/profitability",
    workingOut: () => "/dashboard/workingout",
    incomeToEmployee: () => "/dashboard/income-to-employee",
    overhead: {
      absolute: () => "/dashboard/overhead/absolute",
      relative: () => "/dashboard/overhead/relative",
    },
    print: () => `/dashboard/print`,
  },
  orders: {
    spendings: {
      list: (id: string) => `/orders/${id}/spendings`,
      // точечные операции: сохраняется одна строка затрат или один плановый платёж
      create: (orderId: string) => `/orders/${orderId}/spendings`,
      update: (orderId: string, id: string) => `/orders/${orderId}/spendings/${id}`,
      remove: (orderId: string, id: string) => `/orders/${orderId}/spendings/${id}`,
      // подробности фактических оплат строки — грузятся по наведению
      paymentsInfo: (orderId: string, id: string) => `/orders/${orderId}/spendings/${id}/payments-info`,
      planPayments: {
        create: (orderId: string, id: string) => `/orders/${orderId}/spendings/${id}/plan-payments`,
        update: (orderId: string, id: string, guid: string) =>
          `/orders/${orderId}/spendings/${id}/plan-payments/${guid}`,
        remove: (orderId: string, id: string, guid: string) =>
          `/orders/${orderId}/spendings/${id}/plan-payments/${guid}`,
      },
    },
    orderPortfolio: {
      update: (id: string) => `/orders/portfolio/${id}`,
      delete: (id: string) => `/orders/portfolio/${id}`,
      create: () => "/orders/portfolio",
    },
    detailedList: "/orders/detailed-list",
    reportOrderYear: "orders/order-year-report",
    workResult: (id: string) => `/orders/${id}/content-tasks/result`,
    workResultComment: (id: string) => `/orders/${id}/content-tasks/result`,
    workResultCommentFile: (id: string) => `/orders/${id}/content-tasks/result/file`,
    workResultFile: (id: string, fileId: string) => `/orders/${id}/content-tasks/result/${fileId}`,
    dictionary: () => "/orders/dictionary",
    financeActPayments: (id: string) => `/orders/finance-money/${id}`,
    summary: {
      details: (id: string) => `/orders/summary/${id}`,
      premiums: (id: string) => `/orders/summary/${id}/premiums`,
      short: (id: string) => `/orders/summary/short/${id}`,
      update: (id: string) => `/orders/summary/${id}`,
      create: () => "/orders/summary",
      delete: (id: string) => `/orders/summary/${id}`,
      remove: (id: string) => `/orders/summary-remove/${id}`,
      /** текущие ошибки, которые мешают завершить договор, — счётчик в шапке карточки */
      completionWarnings: (id: string) => `/orders/${id}/completion-warnings`,
      /** перевести в «Завершен» (режим бога); force — в обход ошибок проверки */
      forceCompletion: (id: string, force: boolean) => `/orders/${id}/force-completion?force=${force}`,
      cancel: (id: string) => `/orders/summary-remove-cancel/${id}`,
      links: (id: string) => `/orders/summary/${id}/links`,
      contentReplacement: (id: string, fromId: string) => `/orders/summary/${id}/content-replacement?fromId=${fromId}`,
      tasks: {
        versions: (id: string) => `/order/wopi/files/${id}/versions`,
        diff: (id: string) => `/order/wopi/files/${id}/diff`,
        fileInfo: (id: string, version = "") =>
          version ? `/order/wopi/files/${id}/version/${encodeURIComponent(version)}` : `/order/wopi/files/${id}`,
        contents: (id: string) => `/order/wopi/files/${id}/contents`,
        copy: (id: string) => `/order/wopi/files/${id}/copy`,
        anchors: "/orders/anchors/technical-task",
      },
    },
    ipds: {
      update: (id: string) => `/orders/ipd/${id}`,
    },
    techIndicators: {
      update: (id: string) => `/orders/tech-indicators/${id}`,
    },
    orderCommunication: {
      update: (id: string) => `/orders/communication/${id}`,
    },
    paymentDate: (id: string) => `/orders/payment/${id}/date`,
    mails: {
      list: "/orders/mails",
      update: (id: string) => `/orders/mails/${id}`,
      create: "/orders/mails",
      assign: (id: string) => `/orders/${id}/assign-mails`,
      remove: "/orders/mails",
    },
    print: {
      outsourcerOrder: (id: string) => `/orders/${id}/print/outsourcer-order`,
      technicalTask: (id: string) => `/orders/${id}/print/technical-task`,
      ipdOrder: (id: string) => `/orders/${id}/print/ipd-order`,
      communication: (id: string) => `/orders/${id}/print/communication`,
      contentOrder: (id: string) => `/orders/${id}/print/content-order`,
      contentInvoice: (id: string) => `/orders/${id}/print/invoice-order`,
      printStages: (id: string) => `/orders/${id}/print/stages`,
    },

    mainPage: {
      requests: "/orders/main-page/requests",
      confirmUpload: (id: string) => `/orders/main-page/confirm/${id}`,
    },

    contentTasks: {
      list: (id: string) => `/orders/${id}/content-tasks`,
      addTask: (id: string, contentGuid: string) => `/orders/${id}/content-tasks/${contentGuid}`,
      addTasks: (id: string) => `/orders/${id}/content-tasks`,
      downloadStructure: (id: string) => `/orders/${id}/content-tasks/structure`,
      downloadSheets: (id: string) => `/orders/${id}/content-tasks/sheet`,
      removeTasks: (id: string) => `/orders/${id}/content-tasks`,
      removeVersion: (id: string, contentGuid: string) => `/orders/${id}/content-tasks/${contentGuid}/files`,
      saveFlag: (id: string, contentGuid: string) => `/orders/${id}/content-tasks/${contentGuid}/state`,
      saveWorkFiles: (id: string, contentGuid: string) => `/orders/${id}/content-tasks/${contentGuid}/files`,
      downloadFilesVersion: (id: string, contentGuid: string, fileId: string, version: number) =>
        `/orders/${id}/content-tasks/${contentGuid}/files?fileId=${fileId}&version=${version}`,
      saveSheet: (id: string, contentGuid: string) => `/orders/${id}/content-tasks/${contentGuid}/sheet`,
      signatures: (id: string, contentGuid: string) => `/orders/${id}/content-tasks/${contentGuid}/signatures`,
      anchors: "/orders/anchors/content-tasks",
      userPendingSignatures: "/orders/content-tasks/user-signatures",
      getSignature: (id: string) => `/orders/content-tasks/get-signature/${id}`,
      submitSignature: "/orders/content-tasks/save-signatures",
      attachSignature: "/orders/content-tasks/attach-signatures",
      clearSignatures: "/orders/content-tasks/clear-signatures",
      saveFilePath: (orderId: string, contentGuid: string) =>
        `/orders/${orderId}/content-tasks/${contentGuid}/save-file-path`,
    },
    orderAgents: {
      list: (id: string) => `/orders/${id}/order-agents`,
      payments: (id: string) => `/orders/${id}/order-agents-payments`,
      saveOrderAgent: (id: string) => `/orders/${id}/order-agents`,
      removeTasks: (id: string) => `/orders/${id}/order-agents`,
    },
    orderConfirm: {
      list: `/orders/order-confirm`,
      update: (id: string) => `/orders/order-confirm/${id}`,
      create: "/orders/order-confirm",
      remove: "/orders/order-confirm",
    },
    orderQuestion: {
      list: `/orders/order-questions`,
      print: `/orders/order-questions/print`,
      update: (id: string) => `/orders/order-questions/${id}`,
      complete: (id: string) => `/orders/order-questions/complete/${id}`,
      create: "/orders/order-questions",
      remove: "/orders/order-questions",
      details: (id: string) => `/orders/order-questions/${id}`,
      saveMessage: (id: string) => `/orders/order-question-message/${id}`,
      deleteMessage: (id: string) => `/orders/delete-order-question-message/${id}`,
      move: (id: string) => `/orders/order-questions/move/${id}`,
      moveToBoard: (id: string) => `/orders/order-questions/board/${id}`,
      children: (id: string) => `/orders/order-questions/children/${id}`,
      readiness: (id: string) => `/orders/order-questions/readiness/${id}`,
      board: `/orders/question-board`,
      search: `/orders/question-list`,
      boardColumns: `/orders/question-board/board-columns`,
      cards: `/orders/question-board/cards`,
      columns: `/orders/question-board/columns`,
      portfolio: `/orders/question-portfolios`,
      removePortfolio: (id: string) => `/orders/question-portfolios/${id}`,
    },
    logs: `/orders/logs`,
    stages: {
      list: (id: string) => `/orders/${id}/stages`,
      listIpd: (id: string) => `/orders/${id}/ipdStages`,
      update: (id: string, guid: string) => `/orders/${id}/stages/${guid}`,
      delete: (id: string, guid: string) => `/orders/${id}/stages/${guid}`,
      savelog: (id: string, guid: string) => `/orders/${id}/stageslog/${guid}`,
      create: (id: string) => `/orders/${id}/stages/`,
      comlpetions: {
        list: (id: string) => `/orders/${id}/stages/completions`,
      },
      expertises: {
        list: (id: string) => `/orders/${id}/stages/expertises`,
      },
      sentExpertises: {
        list: (id: string) => `/orders/${id}/stages/sentExpertises`,
      },
      consignments: {
        list: (id: string) => `/orders/${id}/stages/consignments`,
      },
      payments: {
        list: (id: string) => `/orders/${id}/stages/payments`,
        save: (id: string, guid: string) => `/orders/${id}/stages/${guid}/payments`,
        match: (id: string) => `/orders/${id}/stages/payments/match`,
        drop: (id: string) => `/orders/${id}/stages/payments/drop`,
        link: (id: string, stage: string) => `/orders/${id}/stages/${stage}/payments/link`,
      },
      orphans: (id: string) => `/orders/${id}/stages/orphans`,
      timeline: (id: string) => `/orders/${id}/timeline`,
    },
  },
  outsourcers: {
    ...crud("outsourcers"),
    removeRequestAgent: (id: string) => `/outsourcers/${id}/remove-request-agent`,
    credentials: (id: string) => `/outsourcers/${id}/credentials`,
    requestAgentAdd: (id: string) => `/outsourcers/${id}/request-agent`,
    print: () => `/outsourcers/print`,
    self: () => `/outsourcers/self`,
    requestsList: () => `/outsourcers/requestList`,
    requestDetails: (id: string) => `/outsourcers/agent-details/${id}`,
    singleAccess: (id: string) => `/outsourcers/${id}/access/single`,
    confirmRequest: (id: string) => `/outsourcers/${id}/confirm`,
    approve: (id: string) => `/outsourcers/${id}/approve`,
    approveAgentRequest: (id: string) => `/outsourcers/${id}/request-agent/approve`,
  },

  agentMail: {
    threads: () => `/agent-mail/threads`,
    thread: (key: string) => `/agent-mail/threads/${key}`,
    read: (key: string) => `/agent-mail/threads/${key}/read`,
    attach: (key: string) => `/agent-mail/threads/${key}/attach`,
    send: () => `/agent-mail/send`,
    resend: () => `/agent-mail/resend`,
    sync: () => `/agent-mail/sync`,
    unread: (orderId?: string) => `/agent-mail/unread${orderId ? `?orderId=${orderId}` : ""}`,
  },
  suppliers: {
    ...crud("suppliers"),
    listNoRules: () => `/suppliers/listNoRules`,
    listNoRulesAgents: () => `/suppliers/listNoRulesAgents`,
  },
  clients: {
    ...crud("clients"),
    credentials: (id: string) => `/clients/${id}/credentials`,
    print: (id: string) => `/clients/${id}/print`,
  },
  bankDetails: {
    ...crud("bank-details"),
    banks: (field: string, query: string) =>
      `/bank-details/banks?field=${field}&query=${encodeURIComponent(query)}`,
  },
  workTypes: crud("work-types"),
  projectPortfolios: crud("project-portfolios"),
  orderTypes: crud("order-types"),
  orderStatuses: crud("order-statuses"),
  productionStages: crud("production-stages"),
  ipdTypes: crud("ipd-types"),
  agentsCategories: { ...crud("agents-categories"), listNoRules: () => `/agents-categories/listNoRules` },
  orderIndicators: crud("order-indicators"),
  indicators: crud("indicators"),
  overheadTypes: crud("overhead-types"),
  employeePositions: {
    ...crud("employee-positions"),
    singleAccess: (id: string) => `/employee-positions/${id}/access/single`,
    wholeAccess: (id: string) => `/employee-positions/${id}/access/whole`,
  },
  materialValues: {
    ...crud("material-values"),
    stop: (id: string) => `/material-values/stop/${id}`,
    restore: (id: string) => `/material-values/restore/${id}`,
    employer: (id: string) => `/material-values/employer/${id}`,
  },
  employee: {
    ...crud("employee"),
    restore: (id: string) => `/employee/${id}/restore`,
    premiums: (id: string) => `/employee/${id}/premiums`,
    removed: "/employee/list/removed",
    profile: (id: string) => `/employee/${id}/profile`,
    saveProfile: (id: string) => `/employee/${id}/saveprofile`,
    transfer: (id: string) => `/employee/${id}/transfer`,
    access: "/employee/access",
    singleAccess: (id: string) => `/employee/${id}/access/single`,
    godMode: (id: string) => `/employee/${id}/god-mode`,
    credentials: (id: string) => `/employee/${id}/credentials`,
    ownSpendings: (id: string) => `/employee/${id}/own-spendings`,
    engineer: (id: string) => `/employee/${id}/engineer`,
    printUserDocuments: (id: string) => `/employee/${id}/documents`,
    printAllDocuments: `/employee/documents`,
    documentNotes: "/employee/documents/notes",
    signaturesFiles: () => `/employee/signatures-files`,
    saveFiles: () => `/employee/save-signatures-files`,
    saveSigners: () => `/employee/save-signers`,
    downloadSignatureVersion: (fileId: string, version: number) => `/employee/download-signature-version?fileId=${fileId}&version=${version}`,
    removeFileVersion: () => `/employee/remove-signature-version`,
    getSignature: (id: string) => `/employee/get-signature/${id}`,
    submitSignature: "/employee/save-signature",
    exportWorkTime: "/employee/export/work-time",
    bot: {
      register: "/employee/bot/register",
    },
  },
  departments: crud("departments"),
  orgStructure: {
    tree: () => "/org-structure/tree",
    log: (before?: string, limit = 50) =>
      `/org-structure/log?limit=${limit}${before ? `&before=${encodeURIComponent(before)}` : ""}`,
    managed: (employerId: string) => `/org-structure/managed/${employerId}`,
    saveUnit: () => "/org-structure/units",
    saveStaff: () => "/org-structure/units/staff",
    moveUnit: () => "/org-structure/units/move",
    deleteUnit: (id: string) => `/org-structure/units/${id}`,
    moveEmployee: () => "/org-structure/employees/move",
    revertEmployeeMove: () => "/org-structure/employees/move/revert",
    saveType: () => "/org-structure/types",
    deleteType: (id: string) => `/org-structure/types/${id}`,
  },
  categories: crud("material-values/category"),
  deprGroups: crud("material-values/group"),
  tokens: {
    list: "/tokens/list",
    confirm: (id: string) => `/tokens/${id}/confirm`,
    block: (id: string) => `/tokens/${id}/block`,
  },
  device: {
    status: "/device/status",
    code: "/device/code",
    verify: "/device/verify",
  },
  userAccess: {
    list: "/user-access",
    block: (target: string, id: string) => `/user-access/${target}/${id}/block`,
    unblock: (target: string, id: string) => `/user-access/${target}/${id}/unblock`,
  },
  accessLinks: {
    employee: (id: string) => `/access/employee/${id}`,
    employeeLink: (id: string) => `/access/employee/${id}/link`,
    outsourcer: (id: string) => `/access/outsourcer/${id}`,
    outsourcerLink: (id: string) => `/access/outsourcer/${id}/link`,
    employeeCredentialsMail: (id: string) => `/access/employee/${id}/credentials-mail`,
    outsourcerCredentialsMail: (id: string) => `/access/outsourcer/${id}/credentials-mail`,
    check: "/access/link/check",
    accept: "/access/link/accept",
    forgot: "/access/forgot",
  },
  auth: {
    login: "/auth/login",
    logout: "/auth/logout",
  },
  noAuth: {
    ...crud("noAuth"),
    upload: "/noAuth/upload",
    regions: "/noAuth/regions",
  },
  application: {
    metadata: "/application/metadata",
    changelog: (version = "", limit = 15) => `/application/changelog?version=${version}&limit=${limit}`,
    announces: () => `/application/announces`,
    subscribe: "/application/subscribe",
    unsubscribe: "/application/unsubscribe",
    subscriptionsCount: "/application/subscriptions/count",
    notificationSettings: "/application/notification-settings",
    startSettings: "/application/start-settings",
    files: {
      download: `/application/files/download`,
      print: `/application/files/print`,
      content: (id: string, name: string, type: "content" | "preview" = "content") =>
        `/application/files/${id}/content/${encodeURIComponent(name)}?type=${type}`,
      image: (id: string, name: string, type = "image") =>
        `/application/files/${id}/image/${encodeURIComponent(name)}?type=${type}`,
      upload: "/application/files",
      copy: "/application/files/copy",
    },
    generate: {
      guid: "/application/generate/guid",
      id: "/application/generate/id",
    },
    print: "/application/print",
    print2: "/application/print/v2",
    batchPrint: "/application/print/batch",
    batchPrint2: "/application/print/v2/batch",
    dictionary: {
      agents: "/application/dictionary/agents",
    },
  },
  timesheet: {
    list: "/timesheet",
    update: "/timesheet",
    comment: "/timesheet/comment",
    batch: "/timesheet/batch",
    export: "/timesheet/export",
    unit: {
      report: "timesheet/employer-spending-report",
    },
  },
  unitEconomics: {
    list: "/unit-economics",
    reason: "/unit-economics/reason",
    benchmarks: "/unit-economics/benchmarks",
  },
  workload: {
    list: "/workload",
    range: "/workload/range",
    update: "/workload",
    tasks: "/workload/tasks",
    batch: "/workload/batch",
    reconcile: "/workload/reconcile",
    sections: "/workload/sections",
    sectionUsage: "/workload/section-usage",
    comment: "/workload/comment",
  },
  projectPlan: {
    range: "/project-plan/range",
    milestones: (orderId: string) => `/project-plan/milestones/${orderId}`,
    saveMilestone: "/project-plan/milestones",
    removeMilestone: (orderId: string, id: string) => `/project-plan/milestones/${orderId}/${id}`,
    draft: "/project-plan/draft",
    draftApply: "/project-plan/draft/apply",
    conflictMarks: "/project-plan/conflict-marks",
    conflictMarksRemove: "/project-plan/conflict-marks/remove",
    links: "/project-plan/links",
    removeLink: (id: string) => `/project-plan/links/${id}`,
    linkTargets: "/project-plan/link-targets",
    approvals: "/project-plan/approvals",
    approvalsDecide: "/project-plan/approvals/decide",
  },
  schedule: {
    list: "/schedule",
    percents: "/schedule/percents",
    save: (id: string) => `/schedule/${id}`,
    delete: "/schedule",
    recalculate: "/schedule/recalculate",
    overtimeScale: "/schedule/overtime-scale",
    calendar: "/schedule/calendar",
    calendarSync: "/schedule/calendar/sync",
    compensation: (employerId: string) => `/schedule/compensation/${employerId}`,
    print: "/schedule/print",
    vacation: {
      transfer: (userId: string) => `/schedule/vacation/${userId}/transfer`,
    },
    /** «Моя штатка»: своя строка за месяц — любому сотруднику (мобильная версия, specs/005 BR-001) */
    my: "/schedule/my",
    /** Облегчённый список сотрудников за месяц (мобильная версия, specs/005 BR-004) */
    rows: "/schedule/list",
    /** Точечная правка оклада, надбавки и доплат с проверкой версии (specs/005 BR-002, BR-003) */
    patch: (id: string) => `/schedule/${id}/patch`,
  },
  financeRequests: {
    list: "/orders/finance-requests",
    dictionary: "/orders/finance-requests/dictionary",
    toggle: (id: string) => `/orders/finance-requests/toggle/${id}`,
    cancel: (id: string) => `/orders/finance-requests/cancel/${id}`,
    export: "/orders/finance-requests/export",
    // один запрос в любом статусе с историей статусов (по проекту — guid, на накладные — id)
    get: (id: string) => `/orders/finance-requests/${id}`,
    // строка состава затрат запроса по проекту: сумма договора, плановые и фактические оплаты
    article: (id: string) => `/orders/finance-requests/${id}/article`,
    // свои запросы по проекту во всех статусах, постранично
    my: "/orders/finance-requests/my",
  },
  spendings: {
    ...crud("spendings"),
    orderSpendings: (id: string) => `/spendings/order-spendings/${id}`,
    import: {
      todo: "/spendings/import/todo",
      // свежие одобренные запросы на оплату для одной строки выписки
      rowRequests: (id: string) => `/spendings/import/todo/${id}/requests`,
      upload: "/spendings/import",
      commit: "/spendings/import/commit",
      getReturn: "/spendings/import/get-return",
      returnId: "/spendings/import/return",
    },
    export: "/spendings/export",
    overhead: {
      export: "/spendings/overhead/export",
      copy: "/spendings/overhead/copy",
      summary: "/spendings/overhead/summary",
      detailed: (year: string) => `/spendings/overhead/detailed/${year}`,
      plan: "/spendings/overhead/plan",
      cancel: (id: string) => `/spendings/overhead/request/cancel/${id}`,
      toggle: (id: string) => `/spendings/overhead/request/toggle/${id}`,
      requests: () => "/spendings/overhead/request",
    },
  },
  messages: {
    unread: {
      list: "/messages/unread/list",
      count: "/messages/unread/count",
      open: (id: string) => `/messages/open/${id}`,
      openList: "/messages/open",
      openAll: "/messages/open-all",
    },
    list: "/messages/list",
    delete: "/messages",
  },
  knowledgeBase: {
    list: "/knowledge-base/list",
    file: {
      delete: (id: string) => `/knowledge-base/file/${id}`,
      upload: "/knowledge-base/upload",
      rename: (id: string) => `/knowledge-base/file/${id}/rename`,
    },
    folder: {
      delete: (id: string) => `/knowledge-base/folder/${id}`,
      create: "/knowledge-base/folder",
      rename: (id: string) => `/knowledge-base/folder/${id}/rename`,
    },
  },
  news: {
    list: () => "/news/list",
    /** Новости, которые сотрудник должен увидеть плашкой */
    banners: () => "/news/banners",
    open: (id: string) => `/news/${id}/read`,
    update: (id: string) => `/news/${id}`,
    create: () => "/news",
    delete: (id: string) => `/news/${id}`,
  },
  surveys: {
    list: () => "/survey/list",
    create: () => "/survey",
    delete: (id: string, status = "Завершен") => `/survey/${id}/${status}`,
    vote: (id: string) => `/survey/${id}/vote`,
  },
  events: {
    list: () => "/calendar-events/list",
    create: () => "/calendar-events",
    update: (id: string) => `/calendar-events/${id}`,
    delete: (id: string) => `/calendar-events/${id}`,
  },
  settings: {
    update: `/application/settings`,
    networks: `/application/settings/networks`,
    testEmail: `application/test-email`,
    test1c: `/application/test-1c-connection`,
    get: `/application/settings`,
    shortSettings: `/application/shortSettings`,
  },
  onec: {
    resources: `/onec/resources`,
    resourceFields: (resource: string) => `/onec/resources/${encodeURIComponent(resource)}/fields`,
    mapping: `/onec/mapping`,
    preview: (entityKey: string) => `/onec/preview/${entityKey}`,
    import: (entityKey: string) => `/onec/import/${entityKey}`,
  },
  feedback: {
    status: `/feedback/status`,
    branches: `/feedback/branches`,
    messages: `/feedback/messages`,
    unread: `/feedback/unread`,
    read: `/feedback/read`,
    attachments: `/feedback/attachments`,
    attachment: (branchId: string, fileId: string) =>
      `/feedback/branches/${encodeURIComponent(branchId)}/attachments/${encodeURIComponent(fileId)}`,
  },
  templates: {
    technicalTask: {
      fileInfo: (id: string) => `/templates/wopi/files/${encodeURIComponent(id)}`,
      contents: (id: string) => `/templates/wopi/files/${encodeURIComponent(id)}/contents`,
    },
  },
}
